<template>
    <Transition name="fade" mode="out-in">
        <div
            v-if="cart?.lineItems != undefined && cart.lineItems.length > 0"
            data-cy="cartList"
            class="cart-list flex flex-col"
            :class="{
                '': isInteractive,
                'bg-base-100 py-4 px-3': !isInteractive
            }"
        >
            <div
                v-if="isCartErrorTeamwearQty"
                class="alert alert-teamwear"
                :class="mobileOnly ? 'my-6' : 'mb-6'"
            >
                <div>{{ isCartErrorTeamwearQty[0] }}</div>
            </div>

            <template v-if="isInteractive">
                <template v-for="(item, index) in lineItemsWithTeaser" :key="`${refreshListItems}-${index}-${item.type}`">
                    <!-- Line item -->
                    <CartListItem
                        v-if="item.type === 'line-item' && item.lineItem"
                        :line-item="item.lineItem"
                        :is-interactive="isInteractive"
                        :mobile-only="mobileOnly"
                        :add-to-cart-source="addToCartSource"
                        class="relative flex items-start mt-4 gap-4"
                    />

                    <!-- Kevel cart-teaser (reserves height even on no-fill, observed to trigger the fetch) -->
                    <div
                        v-else-if="item.type === 'cart-teaser'"
                        :ref="setKevelTeaserTarget"
                        class="mt-4"
                    >
                        <!-- Loading state -->
                        <div
                            v-if="item.isLoading"
                            class="bg-base-200 animate-pulse aspect-[4/2] md:aspect-[6/1]"
                        />
                        <!-- Ad loaded -->
                        <KevelAdCartTeaser
                            v-else-if="item.ad"
                            :ad="item.ad"
                            :above-the-fold="false"
                        />
                        <!-- No-fill: reserved space -->
                        <div
                            v-else
                            class="bg-base-200 aspect-[4/2] md:aspect-[6/1]"
                            aria-hidden="true"
                        />
                    </div>
                </template>
            </template>
            <template v-else>
                <CartListItem
                    v-for="(lineItem, index) in (props.maxItems ? cart.lineItems.filter((item) => shouldShowInCheckout(item)).slice(0, props.maxItems) : cart.lineItems.filter((item) => shouldShowInCheckout(item)))"
                    :key="`${refreshListItems}-${index}`"
                    :line-item="lineItem"
                    :is-interactive="isInteractive"
                    :mobile-only="mobileOnly"
                    :add-to-cart-source="addToCartSource"
                    class="relative flex items-start mt-6 gap-4"
                />
            </template>
        </div>

        <div v-else>
            <template v-if="$slots.emptyCart">
                <slot name="emptyCart" />
            </template>
            <template v-else>
                {{ $t('cart.list.empty') }}
            </template>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { Schemas } from '#shopware'
import { removeSizekickScriptAndData } from "@/utils/handle-sizekick-product-data"

interface CartListProps {
    isInteractive?: boolean,
    mobileOnly?: boolean,
    sorting?: 'asc' | 'desc',
    maxItems?: number | undefined,
    showOnlyRecentlyAdded?: boolean,
    addToCartSource?: 'cart' | 'added_cart_layer',
}

const props = withDefaults(defineProps<CartListProps>(), {
    isInteractive: true,
    mobileOnly: false,
    sorting: 'desc',
    showOnlyRecentlyAdded: false,
    maxItems: undefined,
    addToCartSource: 'cart',
})

const { cart, recentlyAddedProductId } = useCart()
const { handleSizekickOrderData } = useSizekick()
const refreshListItems = ref(0)

// Kevel cart-teaser ad (hard-coded placement, not from CMS)
const kevelTeaserTarget = ref<HTMLElement | null>(null)
const { ad: kevelTeaserData, isLoading: kevelTeaserLoading, isEnabled: kevelTeaserEnabled } = useKevelPlacement('cart-teaser', { target: kevelTeaserTarget })

// Function ref: the teaser slot lives inside v-for and is re-created on every cart change
function setKevelTeaserTarget(el: Element | ComponentPublicInstance | null) {
    kevelTeaserTarget.value = el instanceof HTMLElement ? el : null
}

const computedLineItems = computed(() => {
    let items
    if (props.sorting === 'asc') {
        // Shopware always sorts line-items by age desc (old -> new)
        // so to sort by age asc (new -> old) array just needs to be reversed
        // BUT line-items of type coupon always should be last in list
        items = cart?.value?.lineItems?.slice().sort((a, b) => {
            if (a.type !== 'product' || b.type !== 'product') {
                return -1
            }

            return 0
        }).reverse()
    } else {
        items = cart?.value?.lineItems
    }

    // Filter to show only recently added item if requested
    if (props.showOnlyRecentlyAdded && recentlyAddedProductId.value && items) {
        const recentlyAddedItem = items.find(item => 
            item.type === 'product' && item.referencedId === recentlyAddedProductId.value
        )
        items = recentlyAddedItem ? [recentlyAddedItem] : items
    }

    // Apply maxItems limit if specified
    if (props.maxItems && items) {
        return items.slice(0, props.maxItems)
    }

    return items
})

// Cart teaser position from env (default: position 2 = 2nd item in list)
const cartTeaserPosition = computed(() => {
    const runtimeConfig = useRuntimeConfig()
    let position = Number(runtimeConfig.public.kevelCartTeaserPosition) || 2

    // Disallow position 1 (ad must appear after at least 1 line item)
    if (position < 2) {
        position = 2
    }

    const itemCount = computedLineItems.value?.length || 0

    // Special handling: if only 1 item, append ad after it (position = 2)
    if (itemCount === 1) {
        return 2
    }

    // Don't exceed item count + 1 (allow appending after last item)
    return Math.min(position, itemCount + 1)
})

// Compute line items with cart-teaser injected at the right position
const lineItemsWithTeaser = computed(() => {
    const items = computedLineItems.value
    if (!items || items.length === 0) {
        return []
    }

    type LineItemWithTeaser = {
        type: 'line-item' | 'cart-teaser'
        index: number
        lineItem?: Schemas['LineItem']
        ad?: ParsedKevelAd | null
        isLoading?: boolean
    }

    const result: LineItemWithTeaser[] = []

    items.forEach((lineItem, index) => {
        result.push({
            type: 'line-item',
            index,
            lineItem,
        })

        // Insert cart-teaser at the configured position (not at all while the zone is disabled)
        // Position number = actual position in list (position 2 = 2nd item)
        // So we insert after (position - 1) items
        if (kevelTeaserEnabled && index + 1 === cartTeaserPosition.value - 1) {
            result.push({
                type: 'cart-teaser',
                index: -1,
                ad: kevelTeaserData.value,
                isLoading: kevelTeaserLoading.value,
            })
        }
    })

    return result
})

watch(() => cart?.value?.lineItems, () => {
    refreshListItems.value++
    if (cart?.value?.lineItems != undefined) {
        handleSizekickOrderData(cart.value.lineItems.filter((lineItem) => lineItem.type === 'product'))
    }
})

const isCartErrorTeamwearQty = computed(() => {
    if (cart?.value?.errors) {
        const matchedMessages = Object.values(cart.value.errors)
            .filter((e) => e.messageKey === "minimum-quantity-not-reached")
            .map((e) => e.message)
        return matchedMessages.length > 0 ? matchedMessages : null
    }
    return null;
})

onMounted(async() => {
    if (cart.value?.lineItems != undefined && cart.value?.lineItems.length > 0) {
        await handleSizekickOrderData(cart.value.lineItems?.filter((lineItem) => lineItem.type === 'product'))
    }
})

onBeforeUnmount(() => {
    removeSizekickScriptAndData()
})

function isCoupon (type: string | undefined) {
    return type === 'promotion' || type === 'easy-coupon'
}

function isFreeGift (lineItem: Schemas['LineItem']) {
    const payloadType = (lineItem.payload as Record<string, unknown> | undefined)?.line_item_type
    return payloadType === "promotion-gift-wrapper"
}

function shouldShowInCheckout (lineItem: Schemas['LineItem']) {
    // Show products and free gifts, hide discount coupons
    return !isCoupon(lineItem.type) || isFreeGift(lineItem)
}
</script>
